import React from "react";
import { MyDayPlan } from "./types";

const formatDuration = (seconds: number): string => {
  const mins = Math.round(seconds / 60);
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
};

const formatDistance = (meters: number): string => {
  if (meters < 1000) return `${Math.round(meters)} m`;
  return `${(meters / 1000).toFixed(1)} km`;
};

const parseTime = (hhmm: string): number => {
  const [h, m] = hhmm.split(":").map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
};

const formatClock = (mins: number) => {
  const wrapped = ((mins % (24 * 60)) + 24 * 60) % (24 * 60);
  const h = Math.floor(wrapped / 60);
  const m = wrapped % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

const legDetail = (durationSeconds: number, distanceMeters: number) =>
  `${formatDuration(durationSeconds)} · ${formatDistance(distanceMeters)}`;

export interface MyDayTimelineProps {
  plan: MyDayPlan | null;
}

const MyDayTimeline: React.FC<MyDayTimelineProps> = ({ plan }) => {
  if (!plan || plan.legs.length === 0) {
    return (
      <div className="p-6 text-sm text-text-grey">
        Plan your day to see a travel timeline.
      </div>
    );
  }

  const morningLegs = plan.legs.filter((l) => l.phase === "morning");
  const eveningLegs = plan.legs.filter((l) => l.phase === "evening");

  const morningTravelMins = morningLegs.reduce(
    (sum, leg) => sum + Math.round((leg.route?.durationSeconds ?? 0) / 60),
    0
  );
  const arriveByMins = parseTime(plan.schedule.arriveByTime);
  const leaveHomeMins = arriveByMins - morningTravelMins;
  const earlyWarning = leaveHomeMins < 5 * 60;

  const events: {
    time: string;
    title: string;
    detail?: string;
    warning?: boolean;
  }[] = [];

  if (morningLegs.length > 0) {
    events.push({
      time: formatClock(leaveHomeMins),
      title: "Leave home",
      detail: plan.stops[0]?.address,
      warning: earlyWarning,
    });

    let clock = leaveHomeMins;
    for (const leg of morningLegs) {
      const durationMins = Math.round((leg.route?.durationSeconds ?? 0) / 60);
      clock += durationMins;

      if (plan.dayType === "parent" && leg.to.type === "school") {
        events.push({
          time: plan.schedule.schoolDropoffTime,
          title: "School drop-off",
          detail: leg.to.address,
        });
      } else if (
        leg === morningLegs[morningLegs.length - 1] &&
        (plan.dayType === "work" || plan.dayType === "student")
      ) {
        const arriveLabel =
          plan.dayType === "student" ? "Arrive at school" : "Arrive at office";
        events.push({
          time: plan.schedule.arriveByTime,
          title: arriveLabel,
          detail: leg.to.address,
        });
      } else if (plan.dayType === "parent" && leg.to.type === "work") {
        events.push({
          time: formatClock(clock),
          title: "Arrive at work",
          detail: `${legDetail(
            leg.route?.durationSeconds ?? 0,
            leg.route?.distanceMeters ?? 0
          )} · ${leg.to.address ?? ""}`.trim(),
        });
      } else {
        events.push({
          time: formatClock(clock),
          title: `Arrive ${leg.to.label}`,
          detail: legDetail(
            leg.route?.durationSeconds ?? 0,
            leg.route?.distanceMeters ?? 0
          ),
        });
      }
    }
  }

  if (eveningLegs.length > 0) {
    const leaveLabel =
      plan.dayType === "student"
        ? "Leave school"
        : plan.dayType === "parent" && plan.legs.some((l) => l.from.type === "work")
          ? "Leave work"
          : plan.dayType === "parent"
            ? "Leave school"
            : "Leave office";

    events.push({
      time: plan.schedule.leaveAtTime,
      title: leaveLabel,
    });

    let clock = parseTime(plan.schedule.leaveAtTime);
    let pickupShown = false;

    for (const leg of eveningLegs) {
      const durationMins = Math.round((leg.route?.durationSeconds ?? 0) / 60);
      clock += durationMins;

      if (
        plan.dayType === "parent" &&
        leg.to.type === "school" &&
        !pickupShown
      ) {
        pickupShown = true;
        events.push({
          time: plan.schedule.schoolPickupTime,
          title: "Pick up from school",
          detail: leg.to.address,
        });
      } else if (leg.to.type === "home") {
        events.push({
          time: formatClock(clock),
          title: "Arrive home",
          detail: legDetail(
            leg.route?.durationSeconds ?? 0,
            leg.route?.distanceMeters ?? 0
          ),
        });
      } else {
        events.push({
          time: formatClock(clock),
          title: `Arrive ${leg.to.label}`,
          detail: legDetail(
            leg.route?.durationSeconds ?? 0,
            leg.route?.distanceMeters ?? 0
          ),
        });
      }
    }
  }

  return (
    <div className="p-6">
      <div className="font-manrope text-xs uppercase tracking-wider text-text-slate mb-4">
        Your day timeline
      </div>
      <ul className="space-y-4">
        {events.map((event, idx) => (
          <li key={idx} className="flex gap-4">
            <span
              className={`font-teko text-xl w-14 shrink-0 ${
                event.warning ? "text-red-500" : "text-brand-yellow"
              }`}
            >
              {event.time}
            </span>
            <div>
              <div className="font-medium text-text-obsidian">{event.title}</div>
              {event.detail && (
                <div className="text-sm text-text-grey">{event.detail}</div>
              )}
              {event.warning && (
                <div className="text-xs text-red-600 mt-1">
                  Very early departure — check your commute time.
                </div>
              )}
            </div>
          </li>
        ))}
      </ul>
      <div className="mt-6 pt-4 border-t border-t-solid border-t-[rgba(0,0,0,0.06)] text-sm text-text-slate">
        Total travel:{" "}
        <span className="font-medium text-text-obsidian">
          {formatDuration(plan.totalDurationSeconds)}
        </span>{" "}
        · {formatDistance(plan.totalDistanceMeters)}
      </div>
    </div>
  );
};

export default MyDayTimeline;
